import React from "react";
import { Button } from "semantic-ui-react";
import { Loading } from "../../components/panels";
import { NoDataAvailableDiv } from "../../components/NoDataAvailableDiv";
import { useManualPagination } from "utilities/hookutils";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { FilterProvider, type FilterValues } from "components/filters/FilterContext";
import FilterHeader from "components/filters/FilterHeader";
import KeywordFilter from "components/filters/Filters/KeywordFilter";
import { Checkbox } from "components/Atoms/inputs/Checkbox";

interface Props {
  excludeUserToClientIds: number[]
  environment: server.dto.Environments
  onUsersSelected: (users: server.dto.UserToClientSmall[]) => void
}

const UserSelectModal = (props: Props) => {
  const maxPerPage = 25;

  const [userToClients, setUsers] = React.useState<Array<server.dto.UserForExternalAdminForList | server.dto.UserForInternalAdminForList>>([]);
  const [keyword, setKeyword] = React.useState<string>("");

  const [selectedUserToClients, setSelectedUsers] = React.useState<Array<server.dto.UserForExternalAdminForList | server.dto.UserForInternalAdminForList>>([]);
  const selectedUsersSet = new Set(selectedUserToClients.map(u => u.userToClientId));
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const onUserSelected = (userToClient: server.dto.UserForExternalAdminForList | server.dto.UserForInternalAdminForList) => {
    if (selectedUsersSet.has(userToClient.userToClientId)) {
      const newList = selectedUserToClients.filter(u => u.userToClientId !== userToClient.userToClientId);
      setSelectedUsers(newList);
    } else {
      setSelectedUsers([...selectedUserToClients, userToClient]);
    }
  };

  const fetchUsers = async (keyword: string, page: number, maxPerPage: number) => {

    let paginationData: server.dto.PaginationData;
    if (props.environment === "internal") {
      const users = await window.CoreApi.UserForInternalAdmin.Paginated({
        keyword,
        excludeKeys: props.excludeUserToClientIds,
        userToClientLifeCycles: ["active"],
        page,
        perPage: maxPerPage
      });
      setUsers(users.data.data);
      pagination.setPagination(users.data.pagination);
      paginationData = users.data.pagination;
    } else {
      const users = await window.CoreApi.UserForExternalAdmin.Paginated({
        keyword,
        excludeKeys: props.excludeUserToClientIds,
        userToClientLifeCycles: ["active"],
        artistIds: [],
        page,
        perPage: maxPerPage
      });
      setUsers(users.data.data);
      pagination.setPagination(users.data.pagination);
      paginationData = users.data.pagination
    }

    setIsLoading(false);

    return paginationData
  };

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    return fetchUsers(keyword, paginationData.page, paginationData.perPage);
  };

  const onFilterChange = async (filter: FilterValues) => {
    setKeyword(filter.keyword.query);
    await fetchUsers(filter.keyword.query, 1, maxPerPage);
  };

  const pagination = useManualPagination(1, maxPerPage, paginate);

  React.useEffect(() => {
    fetchUsers("", 1, maxPerPage);
  }, []);

  return <FilterProvider onFilterChange={onFilterChange}>
    <div>
      <FilterHeader.Filters compact>
        <div className="selectiontool-header-filters">
          <KeywordFilter placeholder={RESX.GeneralLabels.Search} />
        </div>
      </FilterHeader.Filters>
    </div>

    {pagination.pageCount > 1 && <div className="flex justify-end">
      {pagination.Component}
    </div>}

    <div className="grow1 scrollbox-auto">
      {isLoading && <Loading />}
      {!isLoading && (userToClients.length === 0) && <NoDataAvailableDiv />}
      {!isLoading && (userToClients.length > 0) && <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {userToClients.map(userToClient => (
            <div key={userToClient.userToClientId} className="lightgray-bg hoverable" onClick={() => { onUserSelected(userToClient); }}>
              <div>
                <Checkbox onChange={() => { }} checked={selectedUsersSet.has(userToClient.userToClientId)} />
              </div>
              <div className="grow1">
                <UserWithAvatar user={userToClient.user} />
              </div>
              <div>
                {userToClient.freeFieldProfile == null && <div className="red">{RESX.FreeField.NoProfileSelected}</div>}
                {userToClient.freeFieldProfile?.title}
              </div>
            </div>
          ))}
        </div>
      </div>}
    </div>
    <div className="flex justify-end">
      <div>
        <Button primary onClick={(e) => {
          e.preventDefault();
          props.onUsersSelected(selectedUserToClients);
        }}>
          {RESX.Buttons.btnAssign}
        </Button>
      </div>
    </div>
  </FilterProvider>;
};

export default UserSelectModal;